import { useCallback } from "react";
import apiClient from "./apiClient";
import useApiWrapper from "./apiWrapper";
import { getDropdownBranchQuery, getListBranchQuery } from "../utils/branchSession";

/** Put the header's current branch on create/update URLs as ?branch=. */
const appendHeaderBranch = (url) => {
  if (!url) return url;
  const raw = String(url);
  const qIndex = raw.indexOf("?");
  const path = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
  const params = new URLSearchParams(qIndex >= 0 ? raw.slice(qIndex + 1) : "");
  // Company-wide CustomConfiguration (NABH, branch mgmt) must not inherit the
  // header branch — that path only updates payroll/attendance fields.
  if (params.get("company_wide")) {
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }
  const branchQuery = getListBranchQuery();
  if (!branchQuery.branch) return url;
  if (!params.get("branch")) {
    params.set("branch", String(branchQuery.branch));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/** --------------------------
 *  Build Query Params
 *  ------------------------- */
const isDropdownRequest = (filters = {}) =>
    filters.drop_down === "True" ||
    filters.drop_down === true ||
    filters.dropdown === true;

const buildUrl = (baseUrl, filters = {}) => {
  const raw = String(baseUrl || "");
  const qIndex = raw.indexOf("?");
  const path = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
  const queryParams = new URLSearchParams(
    qIndex >= 0 ? raw.slice(qIndex + 1) : ""
  );

  const merged = { ...filters };
  const branchQuery = isDropdownRequest(merged)
    ? getDropdownBranchQuery()
    : getListBranchQuery();
  if (!merged.branch && branchQuery.branch && !merged.company_wide) {
    Object.assign(merged, branchQuery);
  }

  Object.entries(merged).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      const paramKey = key === "dropdown" ? "drop_down" : key;
      const paramValue = key === "dropdown" ? true : value;
      queryParams.set(paramKey, paramValue);
    }
  });

  const qs = queryParams.toString();
  return qs ? `${path}?${qs}` : path;
};

/** --------------------------
 *  useApiMethods Hook
 *  ------------------------- */
const useApiMethods = () => {
  const { apiWrapper } = useApiWrapper();

  const request = useCallback((method, url, data = null, options = {}, setLoading) => {
    return apiWrapper(() =>
      apiClient.request({
        method,
        url,
        data,
        ...options,
      }),
      setLoading
    );
  }, [apiWrapper]);

  const fetch = useCallback((url, filters = {}, setLoading, signal) => {
    const URL = buildUrl(`/${url}`, filters);
    return request("get", URL, null, signal ? { signal } : {}, setLoading);
  }, [request]);

  const create = useCallback((url, data, isFormData = false, setLoading, extraOptions) => {
    const headers = {};
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    } else if (!(typeof FormData !== "undefined" && data instanceof FormData)) {
      headers["Content-Type"] = "multipart/form-data";
    }
    const options = { headers };
    if (extraOptions && typeof extraOptions === "object" && !Array.isArray(extraOptions)) {
      Object.assign(options, extraOptions);
    }
    return request("post", appendHeaderBranch(url), data, options, setLoading);
  }, [request]);

  const update = useCallback((url, data, isFormData = false, setLoading) => {
    const headers = {};
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    } else if (!(typeof FormData !== "undefined" && data instanceof FormData)) {
      headers["Content-Type"] = "multipart/form-data";
    }
    return request("put", appendHeaderBranch(url), data, { headers }, setLoading);
  }, [request]);

  const remove = useCallback((url, data, setLoading) => {
    return request("delete", appendHeaderBranch(url), data, {}, setLoading);
  }, [request]);

  return { fetch, create, update, remove };
};

export default useApiMethods;
